import React from 'react'
import queryString from 'query-string'
import * as R from 'ramda'
import { t } from 'ttag'

import { DateCell } from 'components/shared/Table/common/Cells/DateCell/DateCell'
import { HeaderCell } from 'components/shared/Table/common/Cells/HeaderCell/HeaderCell'
import { JobProgressCell } from 'components/shared/Table/common/Cells/JobProgressCell/JobProgressCell'
import { Pagination } from 'components/shared/Table/common/Pagination/Pagination'
import { PrimaryCell } from 'components/shared/Table/common/Cells/PrimaryCell/PrimaryCell'
import { TextCell } from 'components/shared/Table/common/Cells/TextCell/TextCell'
import { Table } from 'components/shared/Table/Table'

import { getDefaultQueryParams } from 'components/pages/Jobs/model'
import { checkActiveModule } from 'utils/modules'
import { useJobsTable } from './hooks'
import { getTab, getFiltersConfigs } from './model'
import jobStyles from '../../JobsListing.module.scss'
import styles from '../../../JobDetails/CandidatesTable/CandidatesTable.module.scss'

import { getIsFilteredResults } from '../../../helpers'

const mapSortingPropToQueryParam = {
  candidates_count: 'candidates_count',
  department: 'department',
  name: 'name',
  positions: 'positions',
  start_date: 'start_date',
  selection_stage: 'selection_stage',
  updated: 'updated',
}

export const JobsTable = ({ search, filter, resetFilter }) => {
  const { state, handlers } = useJobsTable({
    mapSortingPropToQueryParam,
    search,
    filter,
  })

  const {
    table: {
      headers,
      model,
      rowSelection,
      isAllRowsSelected,
      isFetching,
      isSomeRowsSelected,
    },
    pagination: { currentPage, pageCount, perPage },
  } = state
  const {
    handleStep,
    pagination: { handlePage, handlePageSize },
    table: { toggleAllRowsSelectedHandler },
  } = handlers

  const placeholder = React.useMemo(() => {
    const isFilteredResults = getIsFilteredResults(getFiltersConfigs(), filter)

    if (isFilteredResults) {
      return {
        title: t`There are no jobs matching the selected filters`,
        text: (
          <div
            className={styles.resetButton}
            onClick={resetFilter}
          >{t`reset filters`}</div>
        ),
      }
    }

    return {
      title: t`No active jobs yet`,
      text: t`Click “+ New job“ to continue`,
    }
  }, [filter])

  return (
    <>
      <Table>
        <Table.Head>
          <Table.Row
            type="head"
            onChange={toggleAllRowsSelectedHandler}
            isChecked={isAllRowsSelected}
            isIndeterminate={isSomeRowsSelected}
            hasCheckbox={R.not(isFetching)}
          >
            {R.map(({ columnDef, getToggleSortingHandler, getIsSorted }) => {
              const { accessorKey, header, alignLeft } = columnDef

              return (
                <HeaderCell
                  key={header}
                  sortType={getIsSorted()}
                  text={header}
                  onClick={getToggleSortingHandler()}
                  hasSorting={accessorKey}
                  className={alignLeft && jobStyles.alignLeft}
                />
              )
            }, headers)}
          </Table.Row>
        </Table.Head>
        <Table.Body placeholder={placeholder} isLoaded={R.not(isFetching)}>
          {R.map(
            ({
              id,
              original,
              getIsSelected,
              getIsSomeSelected,
              getToggleSelectedHandler,
            }) => {
              const {
                candidates_count,
                department,
                description,
                id: resourceId,
                modules,
                name,
                positions,
                selection_stage,
                start_date,
                updated,
                active,
              } = original

              const tab = getTab({ selection_stage })
              const defaultQueryParams = getDefaultQueryParams({
                activeTab: tab,
              })

              const basic = `/projects/${resourceId}/${tab}`
              const tabs = `${basic}?${queryString.stringify(
                defaultQueryParams,
              )}`

              const to = R.equals(selection_stage, 1) ? basic : tabs

              const hasInterviewModule = checkActiveModule(modules?.interview)

              return (
                <Table.Row
                  key={id}
                  type="body"
                  onChange={getToggleSelectedHandler()}
                  isChecked={getIsSelected()}
                  isIndeterminate={getIsSomeSelected()}
                  isSelected={rowSelection[id]}
                  isInactive={!active}
                  hasCheckbox
                >
                  <PrimaryCell
                    primary={name}
                    description={description}
                    to={to}
                  />
                  <TextCell text={department.name} />
                  <TextCell text={positions} />
                  <TextCell text={candidates_count} />
                  <DateCell date={start_date} hasNowLabel />
                  <DateCell date={updated} hasNowLabel />
                  <JobProgressCell
                    progress={selection_stage}
                    type="project"
                    onClick={handleStep(resourceId)}
                    {...{ hasInterviewModule }}
                  />
                </Table.Row>
              )
            },
            model.rows,
          )}
        </Table.Body>
      </Table>
      <Pagination
        onSetPage={handlePage}
        onSetPageSize={handlePageSize}
        {...{ currentPage, pageCount, perPage }}
      />
    </>
  )
}
